import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = "https://reflex-backend.alfaoir1.workers.dev";

export default function UserProfile() {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [profile, setProfile] = useState({
    name: "", email: "", phone: "",
    businessName: "", companyName: "", about: "", services: "",
    serviceArea: "", truckCount: "", teamSize: "",
    website: "", facebook: "", instagram: "", linkedin: "", googleMaps: "",
    profilePicture: "", photos: [], videos: [],
  });
  const [temp, setTemp] = useState({ ...profile });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/login"; return; }
    fetch(`${BACKEND_URL}/user-profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const user = {
            ...data.user,
            photos: Array.isArray(data.user.photos) ? data.user.photos.filter(Boolean) : [],
            videos: Array.isArray(data.user.videos) ? data.user.videos.filter(Boolean) : [],
          };
          setProfile(user);
          setTemp(user);
        } else alert(data.error || "Failed to load profile");
      })
      .catch(() => alert("Server error"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => setTemp({ ...temp, [e.target.name]: e.target.value });

  const uploadFile = async (file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);
    const res = await fetch(`${BACKEND_URL}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!data.success) { alert("Upload failed: " + data.error); return null; }
    return data.fileUrl;
  };

  const handleProfilePicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadFile(file);
    if (url) setTemp(t => ({ ...t, profilePicture: url }));
    setUploading(false);
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingGallery(true);
    const urls = (await Promise.all(files.map(uploadFile))).filter(Boolean);
    setTemp(t => ({ ...t, photos: [...(t.photos || []), ...urls] }));
    setUploadingGallery(false);
  };

  const handleVideos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingGallery(true);
    const urls = (await Promise.all(files.map(uploadFile))).filter(Boolean);
    setTemp(t => ({ ...t, videos: [...(t.videos || []), ...urls] }));
    setUploadingGallery(false);
  };

  const removePhoto = (index) => setTemp(t => ({ ...t, photos: t.photos.filter((_, i) => i !== index) }));
  const removeVideo = (index) => setTemp(t => ({ ...t, videos: t.videos.filter((_, i) => i !== index) }));

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("token");
    const res = await fetch(`${BACKEND_URL}/user-profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(temp),
    });
    const data = await res.json();
    if (data.success) { setProfile({ ...temp }); setTemp({ ...temp }); setEditing(false); }
    else alert(data.error || "Save failed");
    setSaving(false);
  };

  const handleCancel = () => { setTemp({ ...profile }); setEditing(false); };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#fafafa" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: "40px", height: "40px", border: "3px solid #1a1a1a", borderTop: "3px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
        <p style={{ color: "#888", fontSize: "14px", fontFamily: "'SF Pro Display', -apple-system, sans-serif" }}>Loading profile...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ background: "#fafafa", minHeight: "100vh", paddingTop: "80px", fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .edit-btn:hover { background: #1a1a1a !important; color: #fff !important; }
        .save-btn:hover { opacity: 0.85; }
        .field-input:focus { border-color: #1a1a1a !important; outline: none; }
        .social-link:hover { background: #1a1a1a !important; color: #fff !important; }
        .remove-btn:hover { opacity: 1 !important; }
        .tag { display: inline-flex; align-items: center; background: #f0f0f0; color: #1a1a1a; padding: 5px 12px; border-radius: 20px; font-size: 13px; font-weight: 500; margin: 3px; }
        .logout-btn:hover { background: #fff0f0 !important; color: #cc0000 !important; border-color: #ffcccc !important; }
      `}</style>

      <div style={{ maxWidth: "780px", margin: "0 auto", padding: "0 20px 60px" }}>

        {/* ACTION BUTTONS ROW */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingTop: "24px" }}>
          <span style={{ fontSize: "18px", fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.3px" }}>My Business Profile</span>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {!editing ? (
              <button className="edit-btn" onClick={() => setEditing(true)}
                style={{ padding: "9px 20px", background: "#fff", color: "#1a1a1a", border: "1.5px solid #1a1a1a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600, transition: "all 0.2s" }}>
                Edit Profile
              </button>
            ) : (
              <>
                <button className="save-btn" onClick={handleSave} disabled={saving}
                  style={{ padding: "9px 20px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={handleCancel}
                  style={{ padding: "9px 20px", background: "#fff", color: "#666", border: "1.5px solid #e0e0e0", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 500 }}>
                  Cancel
                </button>
              </>
            )}
            <a href={`/business/${encodeURIComponent(profile.name)}`} target="_blank" rel="noreferrer"
              style={{ padding: "9px 20px", background: "#f5f5f5", color: "#1a1a1a", border: "1px solid #e8e8e8", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              Public Profile
            </a>
            <button className="logout-btn" onClick={() => { logout(); window.location.href = "/login"; }}
              style={{ padding: "9px 20px", background: "#fff", color: "#888", border: "1.5px solid #e0e0e0", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 500, transition: "all 0.2s" }}>
              Logout
            </button>
          </div>
        </div>

        {/* PROFILE HEADER CARD */}
        <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e8e8e8", padding: "32px", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "24px", flexWrap: "wrap" }}>

            {/* Avatar */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              {temp.profilePicture ? (
                <img src={temp.profilePicture} alt="Profile"
                  style={{ width: "96px", height: "96px", borderRadius: "50%", objectFit: "cover", border: "2px solid #e8e8e8" }} />
              ) : (
                <div style={{ width: "96px", height: "96px", borderRadius: "50%", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
              )}
              {editing && (
                <label style={{ position: "absolute", bottom: "2px", right: "2px", background: "#1a1a1a", borderRadius: "50%", width: "26px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  <input type="file" accept="image/*" onChange={handleProfilePicture} style={{ display: "none" }} />
                </label>
              )}
              {uploading && (
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "20px", height: "20px", border: "2px solid #fff", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                </div>
              )}
            </div>

            {/* Name & Business */}
            <div style={{ flex: 1, minWidth: "200px" }}>
              {editing ? (
                <>
                  <input name="name" value={temp.name || ""} onChange={handleChange} placeholder="Full Name"
                    className="field-input" style={inputStyle} />
                  <input name="businessName" value={temp.businessName || ""} onChange={handleChange} placeholder="Business Name"
                    className="field-input" style={{ ...inputStyle, marginTop: "10px" }} />
                </>
              ) : (
                <>
                  <h1 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.3px" }}>{profile.name || "Your Name"}</h1>
                  <p style={{ margin: "0 0 8px", fontSize: "15px", color: "#555", fontWeight: 500 }}>{profile.businessName || "Business Name"}</p>
                  {profile.serviceArea && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#888" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {profile.serviceArea}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ABOUT */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>About</h3>
          {editing ? (
            <textarea name="about" value={temp.about || ""} onChange={handleChange}
              placeholder="Tell visitors about your business..." rows={5}
              className="field-input" style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
          ) : (
            <p style={{ color: "#444", lineHeight: 1.75, fontSize: "14px", margin: 0 }}>{profile.about || <span style={{ color: "#bbb" }}>No description added yet.</span>}</p>
          )}
        </div>

        {/* BUSINESS DETAILS */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Business Details</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <Field label="Company Name" name="companyName" value={temp.companyName} editing={editing} onChange={handleChange} />
            <Field label="Service Area" name="serviceArea" value={temp.serviceArea} editing={editing} onChange={handleChange} />
            <Field label="Number of Trucks" name="truckCount" value={temp.truckCount} editing={editing} onChange={handleChange} />
            <Field label="Team Size" name="teamSize" value={temp.teamSize} editing={editing} onChange={handleChange} />
            <Field label="Phone" name="phone" value={temp.phone} editing={editing} onChange={handleChange} />
            <Field label="Email" name="email" value={temp.email} editing={false} onChange={handleChange} />
          </div>
        </div>

        {/* SERVICES */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Services</h3>
          {editing ? (
            <>
              <p style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}>One service per line</p>
              <textarea name="services" value={temp.services || ""} onChange={handleChange}
                placeholder={"Junk Removal\nFurniture Pickup\nDemolition"}
                rows={5} className="field-input" style={{ ...inputStyle, resize: "vertical" }} />
            </>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {profile.services ? (
                profile.services.split("\n").filter(s => s.trim()).map((service, i) => (
                  <span key={i} className="tag">{service.trim()}</span>
                ))
              ) : (
                <span style={{ color: "#bbb", fontSize: "14px" }}>No services listed yet.</span>
              )}
            </div>
          )}
        </div>

        {/* SOCIAL LINKS */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Links & Social</h3>
          {editing ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <Field label="Website" name="website" value={temp.website} editing={editing} onChange={handleChange} placeholder="https://yoursite.com" />
              <Field label="Facebook" name="facebook" value={temp.facebook} editing={editing} onChange={handleChange} placeholder="https://facebook.com/..." />
              <Field label="Instagram" name="instagram" value={temp.instagram} editing={editing} onChange={handleChange} placeholder="https://instagram.com/..." />
              <Field label="LinkedIn" name="linkedin" value={temp.linkedin} editing={editing} onChange={handleChange} placeholder="https://linkedin.com/..." />
              <Field label="Google Maps" name="googleMaps" value={temp.googleMaps} editing={editing} onChange={handleChange} placeholder="Paste Google Maps link" />
            </div>
          ) : (
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {profile.website && <SocialBtn href={profile.website} label="Website" icon={<WebIcon />} />}
              {profile.facebook && <SocialBtn href={profile.facebook} label="Facebook" icon={<FbIcon />} />}
              {profile.instagram && <SocialBtn href={profile.instagram} label="Instagram" icon={<IgIcon />} />}
              {profile.linkedin && <SocialBtn href={profile.linkedin} label="LinkedIn" icon={<LiIcon />} />}
              {profile.googleMaps && <SocialBtn href={profile.googleMaps} label="Maps" icon={<MapIcon />} />}
              {!profile.website && !profile.facebook && !profile.instagram && !profile.linkedin && !profile.googleMaps && (
                <span style={{ color: "#bbb", fontSize: "14px" }}>No links added yet.</span>
              )}
            </div>
          )}
        </div>

        {/* GALLERY */}
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 style={{ ...sectionTitle, marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>Gallery</h3>
            {editing && (
              <div style={{ display: "flex", gap: "8px" }}>
                <label style={uploadBtnStyle}>
                  + Photos
                  <input type="file" accept="image/*" multiple onChange={handlePhotos} style={{ display: "none" }} />
                </label>
                <label style={uploadBtnStyle}>
                  + Videos
                  <input type="file" accept="video/*" multiple onChange={handleVideos} style={{ display: "none" }} />
                </label>
              </div>
            )}
          </div>

          {uploadingGallery && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", color: "#888", fontSize: "13px" }}>
              <div style={{ width: "16px", height: "16px", border: "2px solid #1a1a1a", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              Uploading...
            </div>
          )}

          {(temp.photos || []).length > 0 || (temp.videos || []).length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px" }}>
              {(temp.photos || []).map((url, i) => url && (
                <div key={`p-${i}`} style={{ position: "relative", borderRadius: "10px", overflow: "hidden", aspectRatio: "4/3" }}>
                  <img src={url} alt={`photo-${i}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  {editing && (
                    <button onClick={() => removePhoto(i)} className="remove-btn"
                      style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer", fontSize: "12px", opacity: 0.8, transition: "opacity 0.2s" }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {(temp.videos || []).map((url, i) => url && (
                <div key={`v-${i}`} style={{ position: "relative", borderRadius: "10px", overflow: "hidden" }}>
                  <video src={url} controls style={{ width: "100%", borderRadius: "10px", display: "block" }} />
                  {editing && (
                    <button onClick={() => removeVideo(i)} className="remove-btn"
                      style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer", fontSize: "12px", opacity: 0.8, transition: "opacity 0.2s" }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 20px", background: "#fafafa", borderRadius: "10px", border: "1.5px dashed #e0e0e0" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{ marginBottom: "8px" }}>
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21,15 16,10 5,21"/>
              </svg>
              <p style={{ color: "#bbb", fontSize: "14px", margin: 0 }}>No photos or videos yet</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ===== SVG ICONS =====
const WebIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
const FbIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
  </svg>
);
const IgIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
  </svg>
);
const LiIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
    <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
);
const MapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

function SocialBtn({ href, label, icon }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="social-link" title={label}
      style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 14px", background: "#f5f5f5", color: "#1a1a1a", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: 500, border: "1px solid #e8e8e8", transition: "all 0.2s" }}>
      {icon} {label}
    </a>
  );
}

// ===== STYLES =====
const cardStyle = {
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid #e8e8e8",
  padding: "28px",
  marginBottom: "16px",
};

const sectionTitle = {
  fontSize: "15px",
  fontWeight: 700,
  color: "#1a1a1a",
  marginBottom: "20px",
  paddingBottom: "12px",
  borderBottom: "1px solid #f0f0f0",
  letterSpacing: "-0.2px",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1.5px solid #e0e0e0",
  fontSize: "14px",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
  fontFamily: "inherit",
  color: "#1a1a1a",
};

const uploadBtnStyle = {
  display: "inline-block",
  padding: "7px 14px",
  background: "#f5f5f5",
  color: "#1a1a1a",
  border: "1.5px solid #e0e0e0",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 500,
};

function Field({ label, name, value, editing, onChange, placeholder }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "11px", color: "#999", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>
      {editing ? (
        <input name={name} value={value || ""} onChange={onChange} placeholder={placeholder || ""}
          className="field-input" style={inputStyle} />
      ) : (
        <p style={{ margin: 0, fontSize: "14px", color: value ? "#1a1a1a" : "#bbb", fontWeight: 500 }}>{value || "—"}</p>
      )}
    </div>
  );
}